/**
 * /admin/installer-organizations
 * ───────────────────────────────
 * Admin surface for installer org structure. "Parent" = a contractor company
 * (contractors row). "Child" = an individual installer/crew member — a
 * contractor_crews row linking a person (profiles.id) to a contractor_id.
 * Lets ops:
 *   - See every parent installer company and, per company, its crew.
 *   - Look up which company a given installer currently belongs to.
 *   - Move an installer to a different parent company (drag-and-drop onto a
 *     parent in the left list, or the "Move" button — both confirm before
 *     committing).
 *   - Edit a parent company's profile.
 *
 * This page does NOT create installers or companies — those are created by
 * the separate installer-facing website's onboarding flow. This is purely a
 * view + reassign surface over data that already exists.
 *
 * Deliberately NOT under /execution/* — that domain is hard-locked read-only
 * (see src/data/executionDomain.ts: "No new writers", enforced by
 * executionDomain*.test.ts). This page writes (reassign, edit), so it lives
 * under /admin/* alongside ComplianceReviewPage.
 *
 * Permission note: the actual reassignment write is gated by contractor_crews'
 * RLS policy (contractor_crews_write_org — caller must have an active
 * org_memberships row with org_role 'owner' or 'manager' for that org). The
 * role check below only controls who sees this page at all; it does not
 * replicate the org_memberships check, so a visible "Move" action can still
 * be rejected by the database — surfaced as a toast if so.
 */
import { useMemo, useState, type DragEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CockpitPage } from "@/components/cockpit/CockpitPage";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Users, ArrowRightLeft, Pencil, Building2, Shield, GripVertical } from "lucide-react";
import { useRole, type UserRole } from "@/data/RoleContext";
import { useReactQueryContractors } from "@/hooks/useReactQueryContractors";
import { ContractorCrewService, type CrewMember } from "@/services/contractorCrew.service";
import { supabase } from "@/lib/supabase";
import { InstallerProfileEditDialog } from "@/components/admin/InstallerProfileEditDialog";
import type { Contractor } from "@/data/types";

const INSTALLER_ORG_ADMIN_ROLES = new Set<UserRole>([
  "super_admin",
  "owner",
  "ops_manager",
  "head_of_installation",
  "sales_manager",
]);

function canManageInstallerOrgs(role: UserRole): boolean {
  return INSTALLER_ORG_ADMIN_ROLES.has(role);
}

function displayName(c: Contractor): string {
  return c.companyName || c.contactName || c.id;
}

const CREW_DRAG_MIME = "application/x-crew-member-id";

export default function InstallerOrganizationsPage() {
  const { role } = useRole();
  const { contractors, loading: contractorsLoading } = useReactQueryContractors();
  const {
    data: crewMembers = [],
    isLoading: crewLoading,
  } = useQuery({
    queryKey: ["contractor-crews"],
    queryFn: () => ContractorCrewService.fetchCrewMembers(),
    staleTime: 60_000,
  });
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();

  const contractorById = useMemo(() => new Map(contractors.map((c) => [c.id, c])), [contractors]);
  const childrenByParent = useMemo(() => {
    const map = new Map<string, CrewMember[]>();
    for (const member of crewMembers) {
      const list = map.get(member.contractorId) ?? [];
      list.push(member);
      map.set(member.contractorId, list);
    }
    for (const list of map.values()) list.sort((a, b) => a.fullName.localeCompare(b.fullName));
    return map;
  }, [crewMembers]);

  const sortedParents = useMemo(
    () => [...contractors].sort((a, b) => displayName(a).localeCompare(displayName(b))),
    [contractors],
  );

  const resolveInitialParentId = (): string | null => {
    const focusId = params.get("focus");
    if (!focusId) return null;
    if (contractorById.has(focusId)) return focusId;
    const member = crewMembers.find((m) => m.id === focusId || m.userId === focusId);
    return member?.contractorId ?? null;
  };

  const [selectedParentId, setSelectedParentId] = useState<string | null>(resolveInitialParentId);
  const [highlightChildId, setHighlightChildId] = useState<string | null>(params.get("focus"));
  const [leftSearch, setLeftSearch] = useState("");
  const [globalQuery, setGlobalQuery] = useState("");
  const [dragOverParentId, setDragOverParentId] = useState<string | null>(null);
  const [reassignTarget, setReassignTarget] = useState<{ member: CrewMember; toParentId: string } | null>(null);
  const [reassignReason, setReassignReason] = useState("");
  const [reassigning, setReassigning] = useState(false);
  const [editingContractor, setEditingContractor] = useState<Contractor | null>(null);

  const selectedParent = selectedParentId ? contractorById.get(selectedParentId) ?? null : null;
  const selectedChildren = selectedParentId ? childrenByParent.get(selectedParentId) ?? [] : [];

  const filteredParents = useMemo(() => {
    if (!leftSearch.trim()) return sortedParents;
    const q = leftSearch.toLowerCase();
    return sortedParents.filter((p) => displayName(p).toLowerCase().includes(q));
  }, [sortedParents, leftSearch]);

  const globalResults = useMemo(() => {
    const q = globalQuery.trim().toLowerCase();
    if (!q) return [];
    const parentHits = sortedParents
      .filter((p) => displayName(p).toLowerCase().includes(q))
      .map((p) => ({ kind: "parent" as const, id: p.id, label: displayName(p) }));
    const childHits = crewMembers
      .filter((m) => m.fullName.toLowerCase().includes(q) || m.email.toLowerCase().includes(q))
      .map((m) => ({ kind: "child" as const, id: m.id, label: m.fullName || m.email }));
    return [...parentHits, ...childHits].slice(0, 8);
  }, [sortedParents, crewMembers, globalQuery]);

  function selectParent(id: string, highlightId?: string) {
    setSelectedParentId(id);
    setHighlightChildId(highlightId ?? null);
    const next = new URLSearchParams(params);
    next.set("focus", highlightId ?? id);
    setParams(next, { replace: true });
  }

  function jumpTo(hit: { kind: "parent" | "child"; id: string }) {
    if (hit.kind === "parent") {
      selectParent(hit.id);
    } else {
      const member = crewMembers.find((m) => m.id === hit.id);
      if (member) selectParent(member.contractorId, member.id);
    }
    setGlobalQuery("");
  }

  async function currentActorId(): Promise<string> {
    const { data } = await supabase.auth.getUser();
    return data.user?.id ?? "unknown";
  }

  function requestReassign(member: CrewMember, toParentId: string) {
    if (member.contractorId === toParentId) return;
    setReassignTarget({ member, toParentId });
    setReassignReason("");
  }

  async function handleConfirmReassign() {
    if (!reassignTarget) return;
    setReassigning(true);
    try {
      const actorId = await currentActorId();
      const result = await ContractorCrewService.reassignCrewMember(
        reassignTarget.member.id,
        reassignTarget.toParentId,
        { id: actorId },
        reassignReason.trim() || undefined,
      );
      if (result.error || !result.crewMember) {
        toast.error(result.error ?? "Failed to move installer");
        return;
      }
      const toParentName = displayName(contractorById.get(reassignTarget.toParentId)!);
      toast.success(`${reassignTarget.member.fullName} moved to ${toParentName}`);
      queryClient.invalidateQueries({ queryKey: ["contractor-crews"] });
      selectParent(reassignTarget.toParentId, reassignTarget.member.id);
      setReassignTarget(null);
      setReassignReason("");
    } finally {
      setReassigning(false);
    }
  }

  function handleDropOnParent(e: DragEvent<HTMLButtonElement>, parentId: string) {
    e.preventDefault();
    setDragOverParentId(null);
    const crewId = e.dataTransfer.getData(CREW_DRAG_MIME);
    if (!crewId) return;
    const member = crewMembers.find((m) => m.id === crewId);
    if (!member) return;
    requestReassign(member, parentId);
  }

  if (!canManageInstallerOrgs(role)) {
    return (
      <div className="max-w-2xl mx-auto py-12 text-center">
        <Card>
          <CardContent className="p-6 space-y-2">
            <Shield className="h-8 w-8 text-warning mx-auto" />
            <p className="text-sm font-semibold">Restricted</p>
            <p className="text-xs text-muted-foreground">
              Installer Organizations is available to Super Admin / Owner / Ops Manager / Head of
              Installation / Sales Manager only.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const loading = contractorsLoading || crewLoading;

  return (
    <CockpitPage title="Installer Organizations" maxWidth="7xl">
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="relative w-full max-w-sm">
            <Search className="h-3.5 w-3.5 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input
              value={globalQuery}
              onChange={(e) => setGlobalQuery(e.target.value)}
              placeholder="Find a company or installer…"
              className="h-9 text-xs pl-8"
            />
            {globalResults.length > 0 && (
              <Card className="absolute z-10 mt-1 w-full overflow-hidden">
                <ul className="divide-y divide-border max-h-64 overflow-y-auto">
                  {globalResults.map((hit) => (
                    <li key={`${hit.kind}-${hit.id}`}>
                      <button
                        type="button"
                        onClick={() => jumpTo(hit)}
                        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-muted/40 text-xs"
                      >
                        <span className="truncate">{hit.label}</span>
                        <Badge variant="outline" className="text-[9px] shrink-0">
                          {hit.kind === "parent" ? "Company" : "Installer"}
                        </Badge>
                      </button>
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground">
            Drag an installer onto a company to move them, or use the Move button.
          </p>
        </div>

        {loading ? (
          <Card className="p-8 text-center text-xs text-muted-foreground">Loading installers…</Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
            {/* Left: parent companies (drop targets) */}
            <Card className="overflow-hidden">
              <CardContent className="p-3 space-y-2">
                <div className="relative">
                  <Search className="h-3 w-3 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
                  <Input
                    value={leftSearch}
                    onChange={(e) => setLeftSearch(e.target.value)}
                    placeholder="Search companies…"
                    className="h-8 text-xs pl-7"
                  />
                </div>
                {filteredParents.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-6">No installer companies found.</p>
                ) : (
                  <ul className="space-y-1 max-h-[60vh] overflow-y-auto">
                    {filteredParents.map((p) => {
                      const childCount = (childrenByParent.get(p.id) ?? []).length;
                      const active = p.id === selectedParentId;
                      const dragOver = dragOverParentId === p.id;
                      return (
                        <li key={p.id}>
                          <button
                            type="button"
                            onClick={() => selectParent(p.id)}
                            onDragOver={(e) => { e.preventDefault(); setDragOverParentId(p.id); }}
                            onDragLeave={() => setDragOverParentId((cur) => (cur === p.id ? null : cur))}
                            onDrop={(e) => handleDropOnParent(e, p.id)}
                            className={`w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-md text-left text-xs transition-colors ${
                              dragOver
                                ? "bg-primary/15 ring-2 ring-primary/40"
                                : active
                                  ? "bg-primary/10 text-primary ring-1 ring-primary/25"
                                  : "hover:bg-muted/50"
                            }`}
                          >
                            <span className="flex items-center gap-1.5 min-w-0">
                              <Building2 className="h-3.5 w-3.5 shrink-0" />
                              <span className="truncate font-medium">{displayName(p)}</span>
                            </span>
                            <Badge variant="outline" className="text-[9px] shrink-0 gap-1">
                              <Users className="h-2.5 w-2.5" /> {childCount}
                            </Badge>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CardContent>
            </Card>

            {/* Right: selected company's crew (draggable) */}
            <Card className="overflow-hidden">
              <CardContent className="p-4 space-y-3">
                {!selectedParent ? (
                  <p className="text-xs text-muted-foreground text-center py-10">
                    Select a company to see its installers.
                  </p>
                ) : (
                  <>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div>
                        <h2 className="text-sm font-semibold">{displayName(selectedParent)}</h2>
                        <p className="text-[11px] text-muted-foreground">{selectedChildren.length} installer(s)</p>
                      </div>
                      <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1" onClick={() => setEditingContractor(selectedParent)}>
                        <Pencil className="h-3 w-3" /> Edit company
                      </Button>
                    </div>

                    {selectedChildren.length === 0 ? (
                      <p className="text-xs text-muted-foreground text-center py-8">
                        No installers under this company yet.
                      </p>
                    ) : (
                      <ul className="divide-y divide-border">
                        {selectedChildren.map((child) => (
                          <li
                            key={child.id}
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.setData(CREW_DRAG_MIME, child.id);
                              e.dataTransfer.effectAllowed = "move";
                            }}
                            className={`flex items-center justify-between gap-2 py-2.5 px-1 cursor-grab active:cursor-grabbing ${
                              highlightChildId === child.id ? "bg-primary/5 ring-1 ring-primary/20 rounded-md" : ""
                            }`}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <GripVertical className="h-3.5 w-3.5 text-muted-foreground/40 shrink-0" />
                              <div className="min-w-0">
                                <div className="text-xs font-medium truncate">{child.fullName}</div>
                                <div className="text-[10px] text-muted-foreground truncate">
                                  {child.email || "—"} · {child.crewRole}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                              <Badge variant="outline" className={`text-[9px] ${child.status === "active" ? "text-success border-success/30" : "text-muted-foreground"}`}>
                                {child.status}
                              </Badge>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-[11px] gap-1"
                                onClick={() => setReassignTarget({ member: child, toParentId: "" })}
                              >
                                <ArrowRightLeft className="h-3 w-3" /> Move
                              </Button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Reassign confirmation dialog — opened by drag-drop (toParentId pre-filled) or the Move button (pick from Select) */}
      <Dialog open={!!reassignTarget} onOpenChange={(open) => !open && setReassignTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Move installer</DialogTitle>
            <DialogDescription>
              {reassignTarget && reassignTarget.toParentId
                ? `Move ${reassignTarget.member.fullName} from ${
                    contractorById.get(reassignTarget.member.contractorId)
                      ? displayName(contractorById.get(reassignTarget.member.contractorId)!)
                      : "their current company"
                  } to ${displayName(contractorById.get(reassignTarget.toParentId)!)}?`
                : reassignTarget && `Move ${reassignTarget.member.fullName} to a different company.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {reassignTarget && !reassignTarget.toParentId && (
              <div className="space-y-1">
                <Label>New company</Label>
                <Select
                  value={reassignTarget.toParentId}
                  onValueChange={(v) => setReassignTarget((cur) => (cur ? { ...cur, toParentId: v } : cur))}
                >
                  <SelectTrigger className="h-9"><SelectValue placeholder="Select a company…" /></SelectTrigger>
                  <SelectContent>
                    {sortedParents
                      .filter((p) => p.id !== reassignTarget.member.contractorId)
                      .map((p) => (
                        <SelectItem key={p.id} value={p.id}>{displayName(p)}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1">
              <Label htmlFor="reassign-reason">Reason (optional)</Label>
              <Input id="reassign-reason" value={reassignReason} onChange={(e) => setReassignReason(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignTarget(null)} disabled={reassigning}>Cancel</Button>
            <Button onClick={handleConfirmReassign} disabled={!reassignTarget?.toParentId || reassigning}>
              {reassigning ? "Moving…" : "Move installer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <InstallerProfileEditDialog
        contractor={editingContractor}
        open={!!editingContractor}
        onOpenChange={(open) => !open && setEditingContractor(null)}
        onSaved={() => queryClient.invalidateQueries({ queryKey: ["contractors"] })}
      />
    </CockpitPage>
  );
}
